import lang from '~/core/lang';
import Logger from './logger';

function stringToRegExp(str: string): RegExp {
    const match = str.match(/^\/(.+)\/([a-z]*)$/i);
    if (!match) return new RegExp(str);

    const pattern = match[1];
    const flags = match[2];
    return new RegExp(pattern, flags);
}

const normalizes: Record<string, boolean> = {};
const validationRules: ValidationRules = {
    required: (value: any) => {
        let valid = false;
        if (typeof value === 'string' || Array.isArray(value)) valid = value.length > 0;
        else if (typeof value === 'number') valid = !Number.isNaN(value);
        else if (typeof value === 'object') valid = value && Object.keys(value).length > 0;

        return {
            valid,
        };
    },
    integer: (value: any) => {
        if (!value)
            return {
                valid: true, // Although this sounds weird - no value (without required rule) means it doesn't need to be present (but needs to be integer)
            };

        return {
            valid: !Number.isNaN(Number(value)),
            normalized: Number(value),
        };
    },
    email: (value: any) => {
        return {
            valid: /\S+@\S+\.\S+/.test(value),
        };
    },
    min: (value: any, input: any) => {
        let subpath = 'numeric';
        if (Array.isArray(value)) {
            subpath = 'array';
            value = value.length;
        } else if (typeof value === 'object') {
            subpath = 'array';
            value = Object.keys(value).length;
        } else if (isNaN(parseInt(value))) {
            subpath = 'string';
            value = value.length;
        }

        return {
            valid: value >= input,
            subpath,
        };
    },
    max: (value: any, input: any) => {
        let subpath = 'numeric';
        if (Array.isArray(value)) {
            subpath = 'array';
            value = value.length;
        } else if (typeof value === 'object') {
            subpath = 'array';
            value = Object.keys(value).length;
        } else if (isNaN(parseInt(value))) {
            subpath = 'string';
            value = value.length;
        }

        return {
            valid: value <= input,
            subpath,
        };
    },
    regex: (value: any, input: any) => {
        const re = new RegExp(stringToRegExp(input));
        return {
            valid: Array.isArray(value) ? value.every((value) => re.test(value)) : re.test(value),
        };
    },
    array: () => ({ valid: true }),
    string: () => ({ valid: true }),
};

function guessType(input: any) {
    if ([undefined, null].includes(input)) return input;

    const number = Number(input);
    if (!isNaN(number)) return number;

    return input;
}

export class Validator {
    static validate(name: string, value: any, ruleStr?: any): ValidatedResponse {
        if (!ruleStr) return {};

        const splitRules = Array.isArray(ruleStr) ? ruleStr : ruleStr.split('|');
        const sortedRules: [string, any?][][] = [[], []];
        for (const rule of splitRules) {
            if (Array.isArray(rule) && rule.length === 2 && typeof rule[1] === 'function') {
                sortedRules[1].push([rule[0], rule[1]]);
                continue;
            }

            const ruleSplit = rule.split(':');
            const ruleName = ruleSplit.shift() as string;
            const ruleData = ruleSplit.join(':');
            if (!validationRules[ruleName]) {
                Logger.warn('Validator', `Unable to validate ${name}: Unknown validation rule ${ruleName}`);
                continue;
            }

            // Sort into two arrays - first one is normalizable rules, and second is everything else.
            // Consider e.g. `max:32|numeric` and `numeric|max:32` on why (in this case they should mean the same).
            sortedRules[normalizes[ruleName] ? 0 : 1].push([ruleName, guessType(ruleData)]);
        }

        let normalized = value;
        for (const rules of sortedRules) {
            const errors: TranslatableMessage[] = [];
            for (const [ruleName, ruleData] of rules) {
                try {
                    const res =
                        typeof ruleData === 'function'
                            ? {
                                  valid: ruleData(normalized),
                                  subpath: undefined,
                                  normalized: undefined,
                              }
                            : validationRules[ruleName](normalized, ruleData);

                    if (!res.valid) {
                        const additionalData: Record<string, any> = {};
                        if (ruleData != undefined) additionalData[ruleName] = ruleData;

                        errors.push([`components.form.validator.${ruleName}${res.subpath ? `.${res.subpath}` : ''}`, additionalData]);
                    } else {
                        if (res.normalized !== undefined) normalized = res.normalized;
                    }
                } catch (err) {
                    Logger.warn('Validator', `Unintended use of a rule ${ruleName} (${ruleData}) with input '${normalized}'?\n${err}`);
                }
            }

            if (errors.length > 0) {
                return {
                    errors,
                };
            }
        }

        return {
            normalized,
        };
    }

    static formValidate(name: string, value: any, rule: any, global?: boolean): { errors?: TranslatableMessage[]; normalized?: any } {
        const { errors, normalized } = Validator.validate(name, value, rule);

        if (errors) {
            // Inject the field name
            const mappedErrors: TranslatableMessage[] = errors.map((error) => {
                if (global) {
                    error[1] = {
                        ...(error[1] || {}),
                        attribute: lang.global.t(`components.form.fields.${name}`).toLowerCase(),
                        count: 2, // Trick i18n to using the pluralized string which is for global errors
                    };
                }

                return error;
            });

            return {
                errors: mappedErrors,
                normalized,
            };
        }

        return {
            normalized,
        };
    }
}
